import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useNotification } from '../../components/ui/NotificationToast';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import QuickActionButton from '../../components/ui/QuickActionButton';
import SearchCommandPalette from '../../components/ui/SearchCommandPalette';
import Button from '../../components/ui/Button';
import { t } from '../../utils/translations';
import { articlesService } from '../../services/dataService';
import { formatDateTime } from '../../utils/dateUtils';

// Import article editor components
import LanguageTabs from './components/LanguageTabs';
import ContentEditor from './components/ContentEditor';
import ArticleSettings from './components/ArticleSettings';
import PreviewModal from './components/PreviewModal';
import AutoSaveIndicator from './components/AutoSaveIndicator';
import PublicationControls from './components/PublicationControls';

const ArticleEditor = () => {
  const { success, error, warning } = useNotification();
  const [searchParams] = useSearchParams();
  const articleId = searchParams.get('id');
  
  // Layout states
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showSettings, setShowSettings] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  // Content states
  const [activeLanguage, setActiveLanguage] = useState('ar');
  const [articleContent, setArticleContent] = useState({
    ar: { title: '', content: '', excerpt: '' },
    en: { title: '', content: '', excerpt: '' },
    tr: { title: '', content: '', excerpt: '' }
  });
  
  const [articleSettings, setArticleSettings] = useState({
    category: '',
    author: 'admin',
    status: 'draft',
    publishDate: '',
    featuredImage: '',
    metaTitle: '',
    metaDescription: '',
    tags: '',
    views: 0,
    comments: 0
  });

  // Auto-save states
  const [lastSaved, setLastSaved] = useState(new Date());
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Check if content exists for each language
  const hasContent = {
    ar: !!(articleContent?.ar?.title || articleContent?.ar?.content),
    en: !!(articleContent?.en?.title || articleContent?.en?.content),
    tr: !!(articleContent?.tr?.title || articleContent?.tr?.content)
  };

  // Load existing article if ID is provided
  useEffect(() => {
    const loadArticle = async () => {
      if (!articleId) return;

      setIsLoading(true);
      try {
        const article = await articlesService.getById(articleId);
        
        // Transform database format to component format
        setArticleContent({
          ar: { 
            title: article?.titleAr || '', 
            content: article?.contentAr || '', 
            excerpt: article?.excerptAr || '' 
          },
          en: { 
            title: article?.titleEn || '', 
            content: article?.contentEn || '', 
            excerpt: article?.excerptEn || '' 
          },
          tr: { 
            title: article?.titleTr || '', 
            content: article?.contentTr || '', 
            excerpt: article?.excerptTr || '' 
          }
        });

        setArticleSettings({
          category: article?.category || '',
          author: article?.author?.username || 'admin',
          status: article?.published ? 'published' : 'draft',
          publishDate: article?.date || '',
          featuredImage: article?.imageUrl || '',
          metaTitle: '',
          metaDescription: '',
          tags: '',
          views: 0,
          comments: 0
        });

        setHasUnsavedChanges(false);
      } catch (err) {
        console.error('Error loading article:', err);
        error(t('articleEditor.failedToLoadArticle'));
      } finally {
        setIsLoading(false);
      }
    };

    loadArticle();
  }, [articleId]);


  // Auto-save functionality
  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const autoSaveTimer = setTimeout(() => {
      handleAutoSave();
    }, 30000); // Auto-save every 30 seconds

    return () => clearTimeout(autoSaveTimer);
  }, [hasUnsavedChanges, articleContent, articleSettings]);

  const handleAutoSave = useCallback(async () => {
    if (!hasUnsavedChanges) return;

    setIsSaving(true);
    try {
      const articleData = transformArticleData();
      
      if (articleId) {
        // Update existing article
        await articlesService.update(articleId, articleData);
      } else {
        // Create new article as draft
        const newArticle = await articlesService.create({
          ...articleData,
          published: false
        });
        
        // Update URL with new article ID
        window.history.replaceState({}, '', `/article-editor?id=${newArticle?.id}`);
      }
      
      setLastSaved(new Date());
      setHasUnsavedChanges(false);
    } catch (err) {
      console.error('Auto-save failed:', err);
    } finally {
      setIsSaving(false);
    }
  }, [hasUnsavedChanges, articleContent, articleSettings, articleId]);

  const handleContentChange = (language, field, value) => {
    setArticleContent(prev => ({
      ...prev,
      [language]: {
        ...prev?.[language],
        [field]: value
      }
    }));
    setHasUnsavedChanges(true);
  };

  const handleSettingsChange = (field, value) => {
    setArticleSettings(prev => ({
      ...prev,
      [field]: value
    }));
    setHasUnsavedChanges(true);
  };

  const handleImageInsert = () => {
    // Simulate image insertion
    const imageUrl = "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&h=400&fit=crop";
    const imageHtml = `<img src="${imageUrl}" alt="Inserted image showing students in classroom setting" style="max-width: 100%; height: auto; margin: 1rem 0;" />`;
    
    const currentContent = articleContent?.[activeLanguage]?.content || '';
    handleContentChange(activeLanguage, 'content', currentContent + imageHtml);
    
    success(t('articleEditor.imageInserted'));
  };

  const handleImageUpload = (file) => {
    // Simulate image upload
    console.log('Uploading image:', file);
    success(t('articleEditor.featuredImageUploaded'));
  };

  const transformArticleData = () => {
    const publishDate = articleSettings?.publishDate 
      ? new Date(articleSettings.publishDate).toISOString()
      : new Date().toISOString();

    return {
      titleAr: articleContent?.ar?.title || '',
      titleEn: articleContent?.en?.title || '',
      titleTr: articleContent?.tr?.title || '',
      contentAr: articleContent?.ar?.content || '',
      contentEn: articleContent?.en?.content || '',
      contentTr: articleContent?.tr?.content || '',
      excerptAr: articleContent?.ar?.excerpt || '',
      excerptEn: articleContent?.en?.excerpt || '',
      excerptTr: articleContent?.tr?.excerpt || '',
      imageUrl: articleSettings?.featuredImage || '',
      category: articleSettings?.category || '',
      published: articleSettings?.status === 'published',
      date: publishDate,
      formattedDateAr: '',
      formattedDateEn: '',
      formattedDateTr: ''
    };
  };

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      const articleData = transformArticleData();
      
      if (articleId) {
        // Update existing article
        await articlesService.update(articleId, articleData);
      } else {
        // Create new article
        const newArticle = await articlesService.create({
          ...articleData,
          published: false // Save as draft
        });
        
        // Update URL with new article ID
        window.history.replaceState({}, '', `/article-editor?id=${newArticle?.id}`);
      }
      
      setLastSaved(new Date());
      setHasUnsavedChanges(false);
      
      success(t('articleEditor.articleSaved'));
    } catch (err) {
      console.error('Error saving article:', err);
      error(t('articleEditor.failedToSave'));
    } finally {
      setIsSaving(false);
    }
  }, [articleId, articleContent, articleSettings, success, error, t]);

  // Handle keyboard shortcuts (after handleSave is defined)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e?.ctrlKey || e?.metaKey) {
        switch (e?.key) {
          case 'k':
            e?.preventDefault();
            setIsSearchOpen(true);
            break;
          case 's':
            e?.preventDefault();
            handleSave();
            break;
          case 'p':
            e?.preventDefault();
            setShowPreview(true);
            break;
          case '1':
            e?.preventDefault();
            setActiveLanguage('ar');
            break;
          case '2':
            e?.preventDefault();
            setActiveLanguage('en');
            break;
          case '3':
            e?.preventDefault();
            setActiveLanguage('tr');
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleSave]);

  const handlePublish = async () => {
    // Validate required fields
    const currentContent = articleContent?.[activeLanguage];
    if (!currentContent?.title || !currentContent?.content) {
      error(t('articleEditor.fillTitleAndContent'));
      return;
    }

    if (!articleSettings?.category) {
      error(t('articleEditor.selectCategory'));
      return;
    }

    setIsSaving(true);
    try {
      const articleData = transformArticleData();
      articleData.published = true; // Set as published
      
      let savedArticle;
      if (articleId) {
        // Update existing article
        savedArticle = await articlesService.update(articleId, articleData);
      } else {
        // Create new article
        savedArticle = await articlesService.create(articleData);
        
        // Update URL with new article ID
        window.history.replaceState({}, '', `/article-editor?id=${savedArticle?.id}`);
      }
      
      handleSettingsChange('status', 'published');
      setLastSaved(new Date());
      setHasUnsavedChanges(false);
      
      success(t('articleEditor.articlePublished'));
    } catch (err) {
      console.error('Error publishing article:', err);
      error(t('articleEditor.failedToPublish'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleSchedule = async () => {
    if (!articleSettings?.publishDate) {
      error(t('articleEditor.selectPublicationDate'));
      return;
    }

    setIsSaving(true);
    try {
      const articleData = transformArticleData();
      articleData.published = false; // Scheduled articles are not published yet
      
      let savedArticle;
      if (articleId) {
        // Update existing article
        savedArticle = await articlesService.update(articleId, articleData);
      } else {
        // Create new article
        savedArticle = await articlesService.create(articleData);
        
        // Update URL with new article ID
        window.history.replaceState({}, '', `/article-editor?id=${savedArticle?.id}`);
      }
      
      handleSettingsChange('status', 'scheduled');
      setLastSaved(new Date());
      setHasUnsavedChanges(false);
      
      success(t('articleEditor.articleScheduled', { date: formatDateTime(new Date(articleSettings.publishDate)) }));
    } catch (err) {
      console.error('Error scheduling article:', err);
      error(t('articleEditor.failedToSchedule'));
    } finally {
      setIsSaving(false);
    }
  };

  const handlePreview = () => {
    const currentContent = articleContent?.[activeLanguage];
    if (!currentContent?.title && !currentContent?.content) {
      warning(t('articleEditor.addContentToPreview'));
      return;
    }
    setShowPreview(true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">{t('articleEditor.loadingArticle')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header 
        onMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        isMenuOpen={isMobileMenuOpen}
      />
      <Sidebar 
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />
      <main className={`transition-all duration-300 ${
        isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'
      } pt-16`}>
        <div className="p-6">
          {/* Header Section */}
          <div className="mb-6">
            <Breadcrumbs />
            
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mt-4 space-y-4 lg:space-y-0">
              <div>
                <h1 className="text-2xl font-bold text-foreground font-arabic">
                  {t('articleEditor.title')}
                </h1>
                <p className="text-muted-foreground mt-1">
                  {t('articleEditor.description')}
                </p>
              </div>

              <div className="flex items-center space-x-3">
                <AutoSaveIndicator
                  lastSaved={lastSaved}
                  isSaving={isSaving}
                  hasUnsavedChanges={hasUnsavedChanges}
                />
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowSettings(!showSettings)}
                  iconName={showSettings ? 'PanelRightClose' : 'PanelRightOpen'}
                  iconSize={16}
                  className="lg:hidden"
                >
                  {showSettings ? t('articleEditor.hideSettings') : t('articleEditor.showSettings')}
                </Button>
              </div>
            </div>
          </div>

          {/* Editor Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Editor Area */}
            <div className={`${showSettings ? 'lg:col-span-2' : 'lg:col-span-3'} space-y-6`}>
              {/* Language Tabs */}
              <div className="flex items-center justify-between">
                <LanguageTabs
                  activeLanguage={activeLanguage}
                  onLanguageChange={setActiveLanguage}
                  hasContent={hasContent}
                />
                
                <div className="flex items-center space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsSearchOpen(true)}
                    iconName="Search"
                    iconSize={16}
                    title="Search (Ctrl+K)"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handlePreview}
                    iconName="Eye"
                    iconSize={16}
                    title="Preview (Ctrl+P)"
                  />
                </div>
              </div>

              {/* Content Editor */}
              <div className="bg-card border border-border rounded-lg p-6">
                <ContentEditor
                  language={activeLanguage}
                  content={articleContent?.[activeLanguage]}
                  onContentChange={handleContentChange}
                  onImageInsert={handleImageInsert}
                  isRTL={activeLanguage === 'ar'}
                />
              </div>

              {/* Mobile Publication Controls */}
              <div className="lg:hidden bg-card border border-border rounded-lg p-4">
                <PublicationControls
                  settings={articleSettings}
                  onSave={handleSave}
                  onPreview={handlePreview}
                  onPublish={handlePublish}
                  onSchedule={handleSchedule}
                  isSaving={isSaving}
                  hasUnsavedChanges={hasUnsavedChanges}
                />
              </div>
            </div>

            {/* Settings Sidebar */}
            {showSettings && (
              <div className="space-y-6">
                {/* Publication Controls */}
                <div className="hidden lg:block bg-card border border-border rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-foreground font-arabic mb-4">
                    {t('articleEditor.publication')}
                  </h3>
                  <PublicationControls
                    settings={articleSettings}
                    onSave={handleSave}
                    onPreview={handlePreview}
                    onPublish={handlePublish}
                    onSchedule={handleSchedule}
                    isSaving={isSaving}
                    hasUnsavedChanges={hasUnsavedChanges}
                  />
                </div>

                {/* Article Settings */}
                <div className="bg-card border border-border rounded-lg p-4">
                  <ArticleSettings
                    settings={articleSettings}
                    onSettingsChange={handleSettingsChange}
                    onImageUpload={handleImageUpload}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Keyboard Shortcuts Help */}
          <div className="mt-8 p-4 bg-muted rounded-lg">
            <h4 className="text-sm font-medium text-foreground mb-2">
              {t('articleEditor.keyboardShortcuts')}
            </h4>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-xs text-muted-foreground">
              <div className="flex items-center space-x-2">
                <kbd className="px-2 py-1 bg-background rounded">Ctrl+S</kbd>
                <span>{t('articleEditor.save')}</span>
              </div>
              <div className="flex items-center space-x-2">
                <kbd className="px-2 py-1 bg-background rounded">Ctrl+P</kbd>
                <span>{t('articleEditor.preview')}</span>
              </div>
              <div className="flex items-center space-x-2">
                <kbd className="px-2 py-1 bg-background rounded">Ctrl+K</kbd>
                <span>{t('common.search')}</span>
              </div>
              <div className="flex items-center space-x-2">
                <kbd className="px-2 py-1 bg-background rounded">Ctrl+1-3</kbd>
                <span>{t('articleEditor.switchLanguage')}</span>
              </div>
            </div>
          </div>
        </div>
      </main>
      {/* Preview Modal */}
      <PreviewModal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        content={articleContent}
        settings={articleSettings}
        activeLanguage={activeLanguage}
      />
      {/* Search Command Palette */}
      <SearchCommandPalette
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
      />
      <QuickActionButton />
    </div>
  );
};

export default ArticleEditor;